import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryService } from "@/api/services/query-service";
import { useCheckoutStore } from "../store/checkout-store";
import { useCheckoutAddresses } from "./use-checkout-addresses";
import { useCheckoutCart } from "./use-checkout-cart";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import type { Address, CheckoutRequest } from "@/api/models/api-types";
import { useModalStore } from "@/store/use-modal-store";
import { QueryKeys } from "@/api/config/query-keys";
import type { CheckoutPayloadForApproval } from "../store/checkout-store";
import { addressFormBridge } from "../utils/address-form-bridge";
import { getAliasField } from "../utils/validation";

/**
 * Checkout hook - Complete order işlemini yönetir
 * Zero props pattern: Tüm state ve actions hook içinde
 */
export const useCheckout = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const { openCartChanges } = useModalStore();
  const queryClient = useQueryClient();

  // Store states
  const {
    open3DSModal,
    openProcessingScreen,
    buyNowOfferId,
    buyNowQuantity,
    openApprovalModal,
  } = useCheckoutStore();

  // Addresses
  const { addresses } = useCheckoutAddresses();

  // Checkout'a özel cart data (normal cart / buy-now ayrımını doğru yönetir)
  const { totalPrice, loadCart } = useCheckoutCart();

  const refreshCheckoutCartSummary = async () => {
    try {
      if (buyNowOfferId) {
        const buyNowQueryKey = [
          ...QueryKeys.CART.items(),
          "buyNow",
          buyNowOfferId,
          buyNowQuantity ?? 1,
        ] as const;
        await queryClient.invalidateQueries({ queryKey: buyNowQueryKey });
        await queryClient.refetchQueries({
          queryKey: buyNowQueryKey,
          type: "active",
        });
        return;
      }

      await loadCart();
    } catch (refreshError) {
      console.error("❌ Failed to refresh checkout cart summary:", refreshError);
    }
  };

  // apiClient 4xx'ı reject etmez, body'yi başarı gibi döner. Ödeme status'u
  // yoksa bu bir iş kuralı hatasıdır (`no_items_selected` vb.) — genel
  // "ödeme işlenirken hata" değil, API'nin kendi mesajı gösterilir.
  const showCheckoutFailure = (source: unknown) => {
    const root = (source ?? {}) as Record<string, unknown>;
    const nested =
      root.data && typeof root.data === "object"
        ? (root.data as Record<string, unknown>)
        : {};
    const code =
      (typeof nested.code === "string" && nested.code) ||
      (typeof root.code === "string" && root.code) ||
      undefined;
    const message =
      (typeof nested.message === "string" && nested.message) ||
      (typeof root.message === "string" && root.message) ||
      undefined;
    const changes = Array.isArray(nested.changes) ? nested.changes : root.changes;

    if (code === "cart_changed") {
      if (Array.isArray(changes) && changes.length > 0) {
        openCartChanges(changes);
        void refreshCheckoutCartSummary();
      } else {
        error(message || t("checkout.paymentError"));
      }
      return;
    }

    if (code === "offer_unavailable") {
      error(message || t("cart.buyNowUnavailable"));
      void refreshCheckoutCartSummary();
      return;
    }

    error(message || t("checkout.paymentError"));
  };

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: (request: CheckoutRequest) => queryService.checkout(request),
    onSuccess: (response) => {
      const responsePayload: any = response;
      const checkoutData = responsePayload?.data || responsePayload;
      const paymentStatus = checkoutData?.status;

      if (!paymentStatus) {
        showCheckoutFailure(responsePayload);
        return;
      }

      if (paymentStatus === "success") {
        // Başarılı ödeme — processing screen aç (polling başlar)
        const orderGroupId = checkoutData?.orderGroupId;
        if (orderGroupId) {
          openProcessingScreen(orderGroupId);
        } else {
          // orderGroupId yoksa toast göster (fallback)
          success(t("checkout.paymentSuccess"));
        }
      } else if (
        paymentStatus === "requires_action" ||
        paymentStatus === "PENDING"
      ) {
        // 3DS doğrulama gerekiyor
        console.log("🔐 3DS verification required, status:", paymentStatus);
        console.log("📄 htmlContent exists:", !!checkoutData?.htmlContent);

        if (checkoutData?.htmlContent) {
          console.log("🔐 Opening 3DS modal via store...");
          open3DSModal(checkoutData.htmlContent);
          console.log("✅ 3DS modal opened!");
        } else {
          console.error("❌ No htmlContent in response");
          error(t("checkout.threeDSError"));
        }
      } else if (paymentStatus === "failed" || paymentStatus === "FAILED") {
        // Ödeme başarısız
        error(checkoutData?.errorMessage || t("checkout.paymentFailed"));
      } else {
        // Bilinmeyen status
        console.warn("⚠️ Unknown payment status:", paymentStatus);
        error(t("checkout.paymentError"));
      }
    },
    onError: (apiError: any) => {
      showCheckoutFailure(apiError?.response?.data || apiError);
    },
  });

  const getLatestAddresses = () => {
    const cached = queryClient.getQueryData<{ data?: Address[] }>(["addresses"]);
    return cached?.data || addresses;
  };

  // Build checkout request from current store state
  const buildCheckoutPayload = (): CheckoutPayloadForApproval | null => {
    const state = useCheckoutStore.getState();
    const latestAddresses = getLatestAddresses();
    const billingAddressId = state.useSameAddressForBilling
      ? state.selectedDeliveryAddressId
      : state.selectedBillingAddressId;

    const deliveryAddress = latestAddresses.find(
      (a) => a._id === state.selectedDeliveryAddressId,
    );
    const billingAddress = latestAddresses.find((a) => a._id === billingAddressId);

    if (!deliveryAddress || !billingAddress) return null;

    const addressPayload = (addr: typeof deliveryAddress) => ({
      label: addr.label,
      firstName: addr.firstName,
      lastName: addr.lastName,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2 || "",
      city: addr.city,
      state: addr.state,
      postalCode: addr.postalCode,
      country: addr.country,
    });

    const payload: CheckoutPayloadForApproval = {
      installment: state.selectedInstallmentNumber || 1,
      shippingAddress: addressPayload(deliveryAddress),
      billingAddress: addressPayload(billingAddress),
      ...(state.buyNowOfferId && {
        buyNow: {
          offerId: state.buyNowOfferId,
          quantity: state.buyNowQuantity ?? 1,
        },
      }),
    };

    if (state.cardMode === "saved" && state.selectedSavedCardToken) {
      payload.cardToken = state.selectedSavedCardToken;
    } else if (state.cardMode === "new" && state.newCardData) {
      payload.card = {
        cardHolderName: state.newCardData.cardHolderName,
        cardNumber: state.newCardData.cardNumber,
        expireYear: state.newCardData.expireYear,
        expireMonth: state.newCardData.expireMonth,
        cvc: state.newCardData.cvc,
        // Alias boşsa alan hiç gönderilmez — kart ekleme formuyla aynı kural
        ...(state.newCardData.saveCard && {
          saveCard: true,
          ...getAliasField(state.newCardData.alias ?? ""),
        }),
      };
    }

    return payload;
  };

  // Execute the actual checkout with a pre-built payload (called after approvals)
  const executeCheckout = async (payload: CheckoutPayloadForApproval) => {
    const checkoutRequest: CheckoutRequest = {
      installment: payload.installment,
      shippingAddress: payload.shippingAddress,
      billingAddress: payload.billingAddress,
      ...(payload.buyNow && { buyNow: payload.buyNow }),
      ...(payload.cardToken && { cardToken: payload.cardToken }),
      ...(payload.card && { card: payload.card }),
    };

    console.log(
      "📤 Checkout payload:",
      JSON.stringify(checkoutRequest, null, 2),
    );

    await checkoutMutation.mutateAsync(checkoutRequest);
  };

  // Complete order handler — checks approvals first, then proceeds
  const completeOrder = async () => {
    const store = useCheckoutStore.getState();
    const deliveryFormOpen =
      store.isAddingNewAddress || store.isEditingDeliveryAddress;

    if (deliveryFormOpen) {
      const saved = await addressFormBridge.submitDelivery();
      if (!saved) {
        store.requestAddressFormFocus();
        error(t("checkout.completeAddressToContinue"));
        return;
      }
    }

    const afterDelivery = useCheckoutStore.getState();
    const billingFormOpen =
      !afterDelivery.useSameAddressForBilling &&
      (afterDelivery.isAddingNewBillingAddress ||
        afterDelivery.isEditingBillingAddress);

    if (billingFormOpen) {
      const saved = await addressFormBridge.submitBilling();
      if (!saved) {
        afterDelivery.requestAddressFormFocus();
        error(t("checkout.completeBillingAddressToContinue"));
        return;
      }
    }

    const latest = useCheckoutStore.getState();

    // Adres validasyonu
    if (!latest.selectedDeliveryAddressId) {
      latest.requestAddressFormFocus();
      error(t("checkout.selectDeliveryAddress"));
      return;
    }

    const billingAddressId = latest.useSameAddressForBilling
      ? latest.selectedDeliveryAddressId
      : latest.selectedBillingAddressId;

    if (!billingAddressId) {
      error(t("checkout.selectBillingAddress"));
      return;
    }

    // Kart validasyonu
    if (latest.cardMode === "saved" && !latest.selectedSavedCardToken) {
      error(t("checkout.selectPaymentMethod"));
      return;
    }
    if (latest.cardMode === "new" && !latest.newCardData) {
      error(t("checkout.cardDetailsRequired"));
      return;
    }
    // Alias burada denetlenmez: DTO'da isteğe bağlı ve "Kayıtlı Kartlarım"
    // formunda da isteğe bağlı. Boşsa payload'a hiç girmez (getAliasField).

    // Adres validasyonu (nesne kontrolü)
    const builtPayload = buildCheckoutPayload();
    if (!builtPayload) {
      error(t("checkout.addressNotFound"));
      return;
    }

    // Approval kontrolü
    try {
      const approvalsData = await queryService.getCheckoutApprovals();
      const pendingMerchants = (approvalsData.data || []).filter(
        (m) => !m.status,
      );

      if (pendingMerchants.length > 0) {
        // Modal'ı açıp payload'ı store'a kaydet — modal onaylandıktan sonra executeCheckout çağrılır
        openApprovalModal(pendingMerchants, builtPayload);
        return;
      }
    } catch (approvalError) {
      console.warn("⚠️ Could not fetch approvals, proceeding with checkout:", approvalError);
      // API hatası durumunda checkout'a devam et (bloklamayalım)
    }

    await executeCheckout(builtPayload);
  };

  return {
    // Data
    totalPrice,

    // States
    isProcessing: checkoutMutation.isPending,

    // Actions
    completeOrder,
    executeCheckout,
  };
};
